import React, { useState } from 'react';
import { Send, Upload, X, AlertCircle, CheckCircle } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { useReportTypes } from '../context/ReportTypeContext';

const ReportingForm = () => {
  const { reportTypes, loading: typesLoading, error: typesError } = useReportTypes();

  const [formData, setFormData] = useState({
    type: '',
    subject: '',
    description: '',
    priority: 'medium',
    email: '',
    attachments: []
  });

  const [errors, setErrors] = useState({});
  const { submitReport, loading, error, success, setError, setSuccess } = useSettings();

  const priorityLevels = [
    { value: 'low', label: 'Low', description: 'Minor issue, no urgency' },
    { value: 'medium', label: 'Medium', description: 'Moderate issue, normal priority' },
    { value: 'high', label: 'High', description: 'Important issue, needs attention' },
    { value: 'urgent', label: 'Urgent', description: 'Critical issue, immediate attention needed' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain'];

    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        setErrors(prev => ({
          ...prev,
          attachments: `File "${file.name}" is too large. Maximum size is 5MB.`
        }));
        return false;
      }
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          attachments: `File "${file.name}" is not a supported format.`
        }));
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...validFiles].slice(0, 3) // Max 3 files
      }));
      setErrors(prev => ({
        ...prev,
        attachments: ''
      }));
    }
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.type) {
      newErrors.type = 'Please select a report type';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.trim().length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters long';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 20) {
      newErrors.description = 'Description must be at least 20 characters long';
    }

    // Email is now required
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      // Prepare a copy of formData with report_id instead of type
      const payload = {
        ...formData,
        report_id: formData.type,
        priority_level: formData.priority,
      };

      // Remove the 'type' key, so backend only receives report_id
      delete payload.type;

      await submitReport(payload);

      // Reset form on success
      setFormData({
        type: '',
        subject: '',
        description: '',
        priority: 'medium',
        email: '',
        attachments: []
      });

      // Scroll to top after successful submission
      window.scrollTo(0, 0);
    } catch (err) {
      console.log('Submit failed:', err);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'urgent': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-6">
      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-sm text-green-800 font-medium">
              Report submitted successfully! We'll review it and get back to you soon.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-800 font-medium">
              Failed to submit report. Please try again.
            </p>
          </div>
        </div>
      )}

      {/* Report Type */}
      <div className="space-y-3">
        <label className="block text-base text-black opacity-80 font-medium">
          What type of issue are you reporting? *
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {reportTypes.map(({ value, label, description }) => (
            <label
              key={value}
              className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                formData.type === String(value)
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input
                type="radio"
                name="report_id"
                value={value}
                checked={formData.type === String(value)}
                onChange={(e) => handleInputChange('type', e.target.value)}
                className="mt-1 text-teal-600 focus:ring-teal-500"
              />
              <div>
                <div className="font-medium text-gray-900">{label}</div>
                <div className="text-sm text-gray-600">{description}</div>
              </div>
            </label>
          ))}
        </div>
        {errors.type && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.type}
          </p>
        )}
      </div>

      {/* Subject */}
      <div className="space-y-2">
        <label
          htmlFor="subject"
          className="block text-base text-black opacity-80 font-medium"
        >
          Subject *
        </label>
        <input
          id="subject"
          type="text"
          value={formData.subject}
          onChange={(e) => handleInputChange('subject', e.target.value)}
          placeholder="Brief summary of the issue"
          className={`w-full px-4 py-3 rounded-lg border-none outline-none text-base text-black placeholder-black placeholder-opacity-40 bg-stone-50 focus:ring-2 transition-colors ${
            errors.subject ? 'focus:ring-red-500 bg-red-50' : 'focus:ring-teal-500'
          }`}
          aria-describedby={errors.subject ? 'subject-error' : undefined}
        />
        {errors.subject && (
          <p id="subject-error" className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.subject}
          </p>
        )}
      </div>

      {/* Priority */}
      <div className="space-y-3">
        <label className="block text-base text-black opacity-80 font-medium">
          Priority Level
        </label>
        <div className="flex flex-wrap gap-2">
          {priorityLevels.map(({ value, label, description }) => (
            <label
              key={value}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                formData.priority === value
                  ? `${getPriorityColor(value)} border-2 border-current`
                  : 'border border-gray-300 hover:border-gray-400'
              }`}
            >
              <input
                type="radio"
                name="priority"
                value={value}
                checked={formData.priority === value}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                className="sr-only"
              />
              <span className="font-medium">{label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <label
          htmlFor="description"
          className="block text-base text-black opacity-80 font-medium"
        >
          Detailed Description *
        </label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Please provide as much detail as possible about the issue..."
          rows={6}
          className={`w-full px-4 py-3 rounded-lg border-none outline-none text-base text-black placeholder-black placeholder-opacity-40 bg-stone-50 focus:ring-2 transition-colors resize-none ${
            errors.description ? 'focus:ring-red-500 bg-red-50' : 'focus:ring-teal-500'
          }`}
          aria-describedby={errors.description ? 'description-error' : 'description-help'}
        />
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span id="description-help">Include steps to reproduce, expected vs actual behavior, etc.</span>
          <span>{formData.description.length}/1000</span>
        </div>
        {errors.description && (
          <p id="description-error" className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.description}
          </p>
        )}
      </div>

      {/* Contact Email - Now Required */}
      <div className="space-y-2">
        <label
          htmlFor="email"
          className="block text-base text-black opacity-80 font-medium"
        >
          Contact Email *
        </label>
        <input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          placeholder="your.email@example.com"
          className={`w-full px-4 py-3 rounded-lg border-none outline-none text-base text-black placeholder-black placeholder-opacity-40 bg-stone-50 focus:ring-2 transition-colors ${
            errors.email ? 'focus:ring-red-500 bg-red-50' : 'focus:ring-teal-500'
          }`}
          aria-describedby={errors.email ? 'email-error' : 'email-help'}
          required
        />
        <p id="email-help" className="text-sm text-gray-600">
          We'll use this email to contact you about your report and provide updates
        </p>
        {errors.email && (
          <p id="email-error" className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.email}
          </p>
        )}
      </div>

      {/* File Attachments */}
      <div className="space-y-3">
        <label className="block text-base text-black opacity-80 font-medium">
          Attachments (Optional)
        </label>

        {/* Upload Area */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-teal-400 transition-colors">
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-2">
            Drag and drop files here, or click to select
          </p>
          <input
            type="file"
            multiple
            accept="image/*,.pdf,.txt"
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg cursor-pointer hover:bg-teal-700 transition-colors"
          >
            <Upload className="w-4 h-4" />
            Choose Files
          </label>
          <p className="text-xs text-gray-500 mt-2">
            Max 3 files, 5MB each. Supported: JPG, PNG, GIF, PDF, TXT
          </p>
        </div>

        {/* Attached Files */}
        {formData.attachments.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Attached Files:</p>
            {formData.attachments.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-teal-100 rounded flex items-center justify-center">
                    <Upload className="w-4 h-4 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-600">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeAttachment(index)}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                  aria-label={`Remove ${file.name}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {errors.attachments && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.attachments}
          </p>
        )}
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 bg-teal-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Submitting Report...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            Submit Report
          </>
        )}
      </button>
    </form>
  );
};

export default ReportingForm;
